import { useState, useCallback, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createJob,
  parseJobUrlPreview,
  researchJobPreview,
  type JobUrlParseFields,
  type JobUrlParseResult,
} from "@/api/jobs";
import { ApiError } from "@/api/client";
import { guessCompanyFromUrl, guessRoleFromUrl, isValidJobUrl } from "@/lib/resolve-job-url";
import { JobsAddBatch } from "@/components/jobs/jobs-add-batch";
import { cn } from "@/cn";

type Step = "paste" | "parsing" | "confirm" | "success" | "error";
type AddMode = "single" | "batch";

function applyParseFieldsToSetters(
  fields: JobUrlParseFields,
  overwrite: boolean,
  setters: {
    setCompany: React.Dispatch<React.SetStateAction<string>>;
    setRole: React.Dispatch<React.SetStateAction<string>>;
    setLocation: React.Dispatch<React.SetStateAction<string>>;
    setCompensation: React.Dispatch<React.SetStateAction<string>>;
    setWorkMode: React.Dispatch<React.SetStateAction<string>>;
    setPayRange: React.Dispatch<React.SetStateAction<string>>;
    setJobDescription: React.Dispatch<React.SetStateAction<string>>;
    setTldr: React.Dispatch<React.SetStateAction<string>>;
    setJobLevel: React.Dispatch<React.SetStateAction<string>>;
    setJobType: React.Dispatch<React.SetStateAction<string>>;
    setIndustry: React.Dispatch<React.SetStateAction<string>>;
    setPostedAt: React.Dispatch<React.SetStateAction<string>>;
  },
) {
  const pick = (next: string | undefined, setter: React.Dispatch<React.SetStateAction<string>>) => {
    if (!next) return;
    setter((prev) => (overwrite || !prev.trim() ? next : prev));
  };

  pick(fields.company, setters.setCompany);
  pick(fields.role, setters.setRole);
  pick(fields.location, setters.setLocation);
  pick(fields.compensation, setters.setCompensation);
  pick(fields.workMode, setters.setWorkMode);
  pick(fields.payRange ?? fields.compensation, setters.setPayRange);
  pick(fields.jobDescription, setters.setJobDescription);
  pick(fields.tldr, setters.setTldr);
  pick(fields.seniority, setters.setJobLevel);
  pick(fields.employmentType, setters.setJobType);
  pick(fields.industry, setters.setIndustry);
  pick(fields.postedAt, setters.setPostedAt);
}

function tierLabel(tier?: JobUrlParseResult["tier"]): string {
  if (tier === "ats") return "ATS";
  if (tier === "unfurl") return "Link preview";
  return "Manual";
}

export function JobsAddPage() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialParseDone = useRef(false);
  const [mode, setMode] = useState<AddMode>(
    searchParams.get("mode") === "batch" ? "batch" : "single",
  );

  const [step, setStep] = useState<Step>(searchParams.has("url") ? "parsing" : "paste");
  const [url, setUrl] = useState(searchParams.get("url") ?? "");
  const [resolvedUrl, setResolvedUrl] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [company, setCompany] = useState(searchParams.get("company") ?? "");
  const [role, setRole] = useState(searchParams.get("role") ?? "");
  const [location, setLocation] = useState("");
  const [compensation, setCompensation] = useState("");
  const [workMode, setWorkMode] = useState("");
  const [payRange, setPayRange] = useState("");
  const [tldr, setTldr] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [jobLevel, setJobLevel] = useState("");
  const [jobType, setJobType] = useState("");
  const [industry, setIndustry] = useState("");
  const [postedAt, setPostedAt] = useState("");
  const [notes, setNotes] = useState("");
  const [research, setResearch] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [existingJobId, setExistingJobId] = useState<string | null>(null);
  const [parseMeta, setParseMeta] = useState<{
    method?: JobUrlParseResult["method"];
    success?: boolean;
    message?: string;
    tier?: JobUrlParseResult["tier"];
    confidence?: JobUrlParseResult["confidence"];
    provider?: string;
  }>({});

  const applyFields = useCallback((fields: JobUrlParseFields, overwrite = true) => {
    applyParseFieldsToSetters(fields, overwrite, {
      setCompany, setRole, setLocation, setCompensation,
      setWorkMode, setPayRange, setJobDescription, setTldr,
      setJobLevel, setJobType, setIndustry, setPostedAt,
    });
  }, []);

  const seedUrlGuesses = useCallback((value: string) => {
    const guessed = guessCompanyFromUrl(value);
    if (guessed) setCompany((c) => c || guessed);
    const guessedRole = guessRoleFromUrl(value);
    if (guessedRole) setRole((r) => r || guessedRole);
  }, []);

  const applyParseResult = useCallback((result: JobUrlParseResult, sourceUrl: string) => {
    if (result.success) {
      applyFields(result.fields);
    } else {
      applyFields(result.fields, false);
      seedUrlGuesses(sourceUrl);
    }
    if (result.canonicalUrl) setCanonicalUrl(result.canonicalUrl);
    if (result.resolvedUrl) setResolvedUrl(result.resolvedUrl);
    setParseMeta({
      method: result.method,
      success: result.success,
      message: result.hint ?? result.error ?? (result.success
        ? "Job details extracted."
        : "Could not extract details — fill in manually or paste the description."),
      tier: result.tier ?? "manual",
      confidence: result.confidence,
      provider: result.provider,
    });
    setStep("confirm");
  }, [applyFields, seedUrlGuesses]);

  const aiParseMutation = useMutation({
    mutationFn: (targetUrl: string) => parseJobUrlPreview(targetUrl, "ai"),
    onSuccess: (result, targetUrl) => applyParseResult(result, targetUrl),
    onError: (err: unknown, targetUrl) => {
      seedUrlGuesses(targetUrl);
      setParseMeta({
        method: "ai",
        success: false,
        tier: "manual",
        message: err instanceof Error
          ? err.message
          : "AI parse failed. Enter details manually or paste the job description.",
      });
      setStep("confirm");
    },
  });

  const offlineParseMutation = useMutation({
    mutationFn: (targetUrl: string) => parseJobUrlPreview(targetUrl, "offline"),
    onSuccess: (result, targetUrl) => applyParseResult(result, targetUrl),
    onError: (err: unknown) => {
      setParseMeta({
        method: "offline",
        success: false,
        tier: "manual",
        message: err instanceof Error ? err.message : "Offline parse failed.",
      });
    },
  });

  const researchMutation = useMutation({
    mutationFn: () =>
      researchJobPreview({
        url: resolvedUrl || canonicalUrl || url,
        company,
        role,
        jobDescription: jobDescription || undefined,
      }),
    onSuccess: (result) => {
      if (result.success) {
        setResearch(result.research);
      } else {
        setParseMeta((prev) => ({
          ...prev,
          message: result.error ?? "Research failed.",
        }));
      }
    },
    onError: (err: unknown) => {
      setParseMeta((prev) => ({
        ...prev,
        message: err instanceof Error ? err.message : "Research failed.",
      }));
    },
  });

  const startAiParse = useCallback(() => {
    if (!isValidJobUrl(url)) return;
    setStep("parsing");
    setParseMeta({});
    aiParseMutation.mutate(url);
  }, [url, aiParseMutation]);

  useEffect(() => {
    if (initialParseDone.current) return;
    if (step === "parsing" && isValidJobUrl(url)) {
      initialParseDone.current = true;
      aiParseMutation.mutate(url);
    }
  }, [step, url, aiParseMutation]);

  const handleUrlChange = useCallback((value: string) => {
    setUrl(value);
    if (isValidJobUrl(value)) seedUrlGuesses(value);
  }, [seedUrlGuesses]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").trim();
    if (isValidJobUrl(pasted)) {
      e.preventDefault();
      setUrl(pasted);
      seedUrlGuesses(pasted);
      initialParseDone.current = true;
      setStep("parsing");
      setParseMeta({});
      aiParseMutation.mutate(pasted);
    }
  }, [seedUrlGuesses, aiParseMutation]);

  const handleContinue = () => {
    if (!isValidJobUrl(url)) return;
    initialParseDone.current = true;
    setStep("parsing");
    setParseMeta({});
    aiParseMutation.mutate(url);
  };

  const addMutation = useMutation({
    mutationFn: () =>
      createJob({
        url: canonicalUrl || url,
        resolvedUrl: resolvedUrl && resolvedUrl !== (canonicalUrl || url) ? resolvedUrl : undefined,
        company,
        role,
        location: location || undefined,
        compensation: compensation || undefined,
        workMode: workMode || undefined,
        payRange: payRange || undefined,
        tldr: tldr || undefined,
        jobDescription: jobDescription || undefined,
        research: research || undefined,
        notes: notes || undefined,
        postedAt: postedAt || undefined,
        jobLevel: jobLevel || undefined,
        jobType: jobType || undefined,
        industry: industry || undefined,
        sourceProvider: parseMeta.provider,
        source: "manual",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      setExistingJobId(null);
      setStep("success");
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { job?: { id?: string; jobId?: string } } | undefined;
        const id = body?.job?.jobId ?? body?.job?.id ?? null;
        setExistingJobId(id);
        setErrorMsg("This job URL is already in your inbox.");
        setStep("error");
        return;
      }
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setErrorMsg(msg);
      setStep("error");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidJobUrl(url)) { setErrorMsg("Please enter a valid URL."); setStep("error"); return; }
    if (!company.trim()) { setErrorMsg("Please enter a company name."); setStep("error"); return; }
    if (!role.trim()) { setErrorMsg("Please enter a job title."); setStep("error"); return; }
    setErrorMsg("");
    addMutation.mutate();
  };

  const handleAddAnother = () => {
    setUrl(""); setResolvedUrl(""); setCanonicalUrl("");
    setCompany(""); setRole(""); setLocation(""); setCompensation("");
    setWorkMode(""); setPayRange(""); setTldr(""); setJobDescription(""); setNotes("");
    setJobLevel(""); setJobType(""); setIndustry(""); setPostedAt("");
    setResearch(""); setErrorMsg(""); setParseMeta({}); setExistingJobId(null);
    initialParseDone.current = false;
    setStep("paste");
  };

  const isParsing = step === "parsing" || aiParseMutation.isPending;
  const isOfflineParsing = offlineParseMutation.isPending;
  const isResearching = researchMutation.isPending;
  const showPasteJdHint =
    parseMeta.tier === "manual" || !jobDescription.trim() || parseMeta.success === false;
  const openUrl = resolvedUrl || canonicalUrl || url;

  return (
    <div className={cn("mx-auto", mode === "batch" ? "max-w-4xl" : "max-w-2xl")}>
      <header className="mb-8">
        <Link to="/jobs" className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to Jobs
        </Link>
        <h1 className="text-2xl font-bold text-foreground">Add a Job</h1>
        <p className="text-muted-foreground">
          {mode === "batch"
            ? "Import many job URLs from a CSV — map columns, enrich from links, then add to your inbox."
            : "Paste a job link for best-effort enrichment. LinkedIn and some boards often need title, company, or a pasted description."}
        </p>
        <div className="mt-4 flex gap-1 border-b border-border">
          {(
            [
              { id: "single" as const, label: "Single" },
              { id: "batch" as const, label: "Batch CSV" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMode(tab.id)}
              className={cn(
                "relative px-4 py-2.5 text-sm font-medium transition",
                mode === tab.id
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
              {mode === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand" />
              )}
            </button>
          ))}
        </div>
      </header>

      {mode === "batch" && <JobsAddBatch />}

      {mode === "single" && step === "paste" && (
        <div className="rounded-lg border border-border bg-surface/30 p-8 text-center">
          <h2 className="mb-2 text-lg font-semibold text-foreground">Paste a job link</h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Works best with Greenhouse, Lever, or Ashby URLs. LinkedIn / Indeed / Glassdoor are saved as links with optional preview — not a board sync.
          </p>
          <div className="mx-auto max-w-xl">
            <input type="url" value={url} onChange={(e) => handleUrlChange(e.target.value)} onPaste={handlePaste}
              onKeyDown={(e) => { if (e.key === "Enter" && isValidJobUrl(url)) { e.preventDefault(); handleContinue(); } }}
              placeholder="https://boards.greenhouse.io/… or https://www.linkedin.com/jobs/view/…"
              className="w-full rounded-lg border border-border bg-bg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" autoFocus />
            {url && isValidJobUrl(url) && (
              <button onClick={handleContinue} className="mt-4 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90">
                Fetch job details
              </button>
            )}
          </div>
        </div>
      )}

      {mode === "single" && step === "parsing" && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
          <h2 className="mb-1 text-lg font-semibold text-foreground">Fetching job details…</h2>
          <p className="text-sm text-muted-foreground">Reading the posting (ATS API or link preview).</p>
          <p className="mt-2 truncate text-xs text-muted-foreground">{url}</p>
        </div>
      )}

      {mode === "single" && step === "confirm" && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md border border-border bg-muted/30 px-2 py-1 text-xs font-medium text-foreground">
              {tierLabel(parseMeta.tier)}
              {parseMeta.confidence ? ` · ${parseMeta.confidence}` : ""}
              {parseMeta.provider ? ` · ${parseMeta.provider}` : ""}
            </span>
            {openUrl && (
              <a
                href={openUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-border px-3 py-2 text-xs font-medium text-brand transition hover:bg-muted/20"
              >
                Open link
              </a>
            )}
            <button
              type="button"
              onClick={startAiParse}
              disabled={isParsing}
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition hover:bg-muted/20 disabled:opacity-50"
            >
              {isParsing ? "Fetching…" : "Re-fetch"}
            </button>
            <button
              type="button"
              onClick={() => offlineParseMutation.mutate(url)}
              disabled={isOfflineParsing || !isValidJobUrl(url)}
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition hover:bg-muted/20 disabled:opacity-50"
            >
              {isOfflineParsing ? "Parsing…" : "Parse offline"}
            </button>
            <button
              type="button"
              onClick={() => researchMutation.mutate()}
              disabled={isResearching || !company.trim() || !role.trim()}
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition hover:bg-muted/20 disabled:opacity-50"
            >
              {isResearching ? "Researching…" : "Research Job"}
            </button>
          </div>

          {parseMeta.message && (
            <div className={`rounded-md px-3 py-2 text-xs ${
              parseMeta.success
                ? "border border-success/30 bg-success/5 text-success"
                : "border border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400"
            }`}>
              {parseMeta.message}
              {parseMeta.method && (
                <span className="ml-1 opacity-70">({parseMeta.method})</span>
              )}
            </div>
          )}

          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Confirm job details</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-foreground">Job URL</label>
                <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
                  <span className="flex-1 truncate">{canonicalUrl || url}</span>
                  <button type="button" onClick={() => { initialParseDone.current = false; setStep("paste"); }} className="shrink-0 text-xs text-brand hover:underline">Change</button>
                </div>
                {resolvedUrl && resolvedUrl !== (canonicalUrl || url) && (
                  <p className="text-xs text-muted-foreground">
                    Using careers/ATS link for apply:{" "}
                    <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" className="text-brand hover:underline break-all">
                      {resolvedUrl}
                    </a>
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">Company <span className="ml-0.5 text-destructive">*</span></label>
                  <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
                    className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">Job Title <span className="ml-0.5 text-destructive">*</span></label>
                  <input type="text" value={role} onChange={(e) => setRole(e.target.value)}
                    className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
                </div>
              </div>

              {company.trim() && (
                <p className="text-xs text-muted-foreground">
                  Prefer a durable ATS board when available —{" "}
                  <Link
                    to={`/companies?q=${encodeURIComponent(company)}`}
                    className="text-brand hover:underline"
                  >
                    search Employers for {company}
                  </Link>
                  {" "}or open Find Jobs → Companies.
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">Location</label>
                  <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                    className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Remote, SF, NY" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">Compensation</label>
                  <input type="text" value={compensation} onChange={(e) => setCompensation(e.target.value)}
                    className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="$150k-$250k" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">Work Mode</label>
                  <input type="text" value={workMode} onChange={(e) => setWorkMode(e.target.value)}
                    className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="Remote, hybrid, onsite" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium text-foreground">Pay Range</label>
                  <input type="text" value={payRange} onChange={(e) => setPayRange(e.target.value)}
                    className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand" placeholder="$150k – $200k" />
                </div>
              </div>

              {tldr && (
                <div className="rounded-lg border border-border p-4">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Summary</h3>
                  <p className="text-sm text-foreground">{tldr}</p>
                </div>
              )}

              <div className="rounded-lg border border-border p-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Job Description
                  {showPasteJdHint ? " — paste if preview is empty" : ""}
                </h3>
                <textarea value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} rows={8}
                  placeholder="Paste the full job description from the posting when the preview is blocked or incomplete…"
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand font-mono" />
              </div>

              {research && (
                <div className="rounded-lg border border-border p-4">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Research Notes</h3>
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">{research}</pre>
                </div>
              )}

              <div className="rounded-lg border border-border p-4">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</h3>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                  placeholder="Optional notes about this job…"
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>

              {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}

              <div className="flex items-center gap-3 pt-2">
                <button type="submit" disabled={addMutation.isPending}
                  className="flex-1 rounded-md bg-brand px-4 py-2.5 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50">
                  {addMutation.isPending ? "Adding…" : "Add Job"}
                </button>
                <button type="button" onClick={() => { initialParseDone.current = false; setStep("paste"); }}
                  className="rounded-md border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition hover:text-foreground">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {mode === "single" && step === "success" && (
        <div className="rounded-lg border border-success/30 bg-success/5 p-8 text-center">
          <h2 className="mb-1 text-lg font-semibold text-foreground">Job added</h2>
          <p className="mb-2 text-sm text-muted-foreground"><strong>{company}</strong> &mdash; {role}</p>
          <p className="mb-6 text-xs text-muted-foreground">View it in your jobs list to evaluate and track.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/jobs" className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90">View Jobs</Link>
            <button onClick={handleAddAnother} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition hover:text-foreground">Add another</button>
          </div>
        </div>
      )}

      {mode === "single" && step === "error" && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-8 text-center">
          <p className="mb-1 text-sm font-medium text-foreground">
            {existingJobId ? "Already in your inbox" : "Could not add job"}
          </p>
          <p className="mb-6 text-sm text-destructive">{errorMsg}</p>
          <div className="flex items-center justify-center gap-3">
            {existingJobId ? (
              <Link to={`/jobs/${existingJobId}`} className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90">
                Open existing job
              </Link>
            ) : (
              <button onClick={() => setStep("confirm")} className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90">Try again</button>
            )}
            <button onClick={handleAddAnother} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition hover:text-foreground">Start over</button>
          </div>
        </div>
      )}
    </div>
  );
}
